/* ============================================================
   pillar-article.js
   Lucius Atelier · Pillar page navy-immersive interactivity

   Responsibilities:
   1. Mount sticky-cases section into article body at the placeholder div
      (.la-pillar-sticky-mount[data-pillar-mount="sticky-cases"])
   2. Mount parallax section into article body at the placeholder div
      (.la-pillar-parallax-mount[data-pillar-mount="ultra-thin-parallax"])
   3. Drive sticky-scroll case progression — observe panels, swap images,
      update progress dots
   4. Drive parallax background offset on scroll
   5. Toggle progress-dot track visibility based on viewport overlap

   Design parallel to pillar-flowchart.js.
   ============================================================ */

(function () {
  'use strict';

  /* ============================================================
     === MOUNTING ===
     Sections render hidden source content; this script moves
     them into the article body at the right marker divs.
     ============================================================ */

  function mountSection(sourceSelector, mountAttr) {
    const sources = document.querySelectorAll(sourceSelector);
    if (!sources.length) return null;

    const mounted = [];
    sources.forEach(source => {
      const id = source.getAttribute('data-mount-id');
      if (!id) return;

      const target = document.querySelector(
        `.la-pillar-${mountAttr}-mount[data-pillar-mount="${id}"]`
      );

      if (!target) {
        // No mount point — render in place
        source.removeAttribute('hidden');
        mounted.push(source);
        return;
      }

      // Move children into target, remove empty source
      while (source.firstChild) {
        target.appendChild(source.firstChild);
      }
      source.parentNode.removeChild(source);
      mounted.push(target);
    });

    return mounted;
  }

  function mountAll() {
    // If the pillar wrapper exists on this page, mark <body> so CSS overrides
    // (old title hide, comment section navy bg, etc.) can scope reliably.
    if (document.querySelector('.la-pillar')) {
      document.body.classList.add('la-pillar-active');
    }

    mountSection('.la-pillar-sticky-source', 'sticky');
    mountSection('.la-pillar-parallax-source', 'parallax');

    // After mount, initialise behaviours
    initStickyScroll();
    initParallax();
  }

  /* ============================================================
     === STICKY-SCROLL ===
     ============================================================ */

  function initStickyScroll() {
    const stickyRoot = document.querySelector('.la-pillar__sticky');
    if (!stickyRoot) return;

    const panels = stickyRoot.querySelectorAll('.la-pillar__case-panel');
    const images = stickyRoot.querySelectorAll('.la-pillar__sticky-image');
    const stickyImageCol = stickyRoot.querySelector('.la-pillar__sticky-image-col');
    const progressTrack = document.querySelector('.la-pillar__progress');
    const dots = progressTrack ? progressTrack.querySelectorAll('.la-pillar__progress-dot') : [];

    if (!panels.length || !images.length) return;

    function setActive(index) {
      images.forEach(img => {
        const i = parseInt(img.getAttribute('data-case-index'), 10);
        img.classList.toggle('is-active', i === index);
      });
      dots.forEach(dot => {
        const i = parseInt(dot.getAttribute('data-dot-index'), 10);
        dot.classList.toggle('is-current', i === index);
        dot.classList.toggle('is-passed', i < index);
      });
    }

    setActive(0);

    let scrollTimer = null;

    function recalc() {
      // Mobile: trigger line = bottom edge of sticky image col.
      // Desktop: trigger line = viewport center.
      // On mobile, the image and text are stacked rather than side-by-side, so
      // we want the swap to happen when the next case's title appears JUST under
      // the image — title and image visible together at the moment of swap.
      const isMobile = window.innerWidth < 900;
      let triggerY;

      if (isMobile && stickyImageCol) {
        const colRect = stickyImageCol.getBoundingClientRect();
        triggerY = colRect.bottom; // bottom of image — title appears just under it
      } else {
        triggerY = window.innerHeight / 2;
      }

      let bestPanel = null;
      let bestDistance = Infinity;

      panels.forEach(panel => {
        const rect = panel.getBoundingClientRect();
        // Mobile: anchor at title position (panel top + 40px, just past the case-num eyebrow).
        // Desktop: anchor at panel center.
        const anchor = isMobile ? rect.top + 40 : rect.top + rect.height / 2;
        const distance = Math.abs(anchor - triggerY);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestPanel = panel;
        }
      });

      if (bestPanel) {
        const idx = parseInt(bestPanel.getAttribute('data-panel-index'), 10);
        setActive(idx);
      }

      // Toggle progress track visibility based on whether sticky section
      // overlaps the viewport's vertical center band
      if (progressTrack) {
        const rect = stickyRoot.getBoundingClientRect();
        const inSection = rect.top < window.innerHeight * 0.3 &&
                          rect.bottom > window.innerHeight * 0.3;
        progressTrack.classList.toggle('is-active', inSection);
      }
    }

    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(recalc, 30);
    }, { passive: true });

    window.addEventListener('resize', recalc);
    recalc();
  }

  /* ============================================================
     === PARALLAX ===
     Subtle Y-translate on the cross-section background as it
     scrolls past the viewport. Capped at -50px range.
     ============================================================ */

  function initParallax() {
    const parallaxBgs = document.querySelectorAll('.la-pillar__parallax-bg');
    if (!parallaxBgs.length) return;

    const items = [];
    parallaxBgs.forEach(bg => {
      const root = bg.parentElement;
      if (root) items.push({ bg, root });
    });

    function update() {
      items.forEach(({ bg, root }) => {
        const rect = root.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > window.innerHeight + 200) return;
        // Stronger parallax: -180px range (was -50). Negative = bg moves opposite scroll.
        const offset = (rect.top / window.innerHeight) * -180;
        bg.style.transform = `translateY(${offset}px)`;
      });
    }

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ============================================================
     === BOOT ===
     ============================================================ */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll);
  } else {
    mountAll();
  }
})();
